import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Modal from 'react-modal';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faBars,
  faXmark,
  faRightFromBracket,
  faChalkboardTeacher,
  faCalendarAlt,
  faCertificate,
  faLock,
  faSun,
  faMoon,
  faBookOpen,
  faTags,
  faArrowUpRightFromSquare,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import './Layout.css';

Modal.setAppElement('#root');

const LOGO_DARK = '/assets/img/logo/logowhitenew.png'; // white logo for dark mode
const LOGO_LIGHT = '/assets/img/logo/logonew.png'; // colored logo for light mode
const THEME_KEY = 'rm-demo-theme';

const readTheme = () => {
  try {
    return localStorage.getItem(THEME_KEY) === 'dark' ? 'dark' : 'light'; // light by default
  } catch {
    return 'light';
  }
};

// Shown locked during the trial: a preview of what enrolled students get
const LOCKED_ITEMS = [
  { icon: faChalkboardTeacher, label: 'My Classes' },
  { icon: faCalendarAlt, label: 'My Schedule' },
  { icon: faCertificate, label: 'My Certificates' },
];

const DemoLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showLeave, setShowLeave] = useState(false);
  const [theme, setTheme] = useState(readTheme);
  const isLight = theme === 'light';

  const toggleTheme = () => {
    const next = isLight ? 'dark' : 'light';
    setTheme(next);
    try {
      localStorage.setItem(THEME_KEY, next);
    } catch {
      // private mode: theme just won't be remembered
    }
  };

  const studentId = sessionStorage.getItem('demoStudentId');
  const PHONE = process.env.REACT_APP_MOBILE_NUMBER1;
  const firstName = userData?.fullName?.split(' ')[0] || '';

  // No code entered (or tab was closed): back to Join Free Class
  useEffect(() => {
    if (!studentId) navigate('/joinfreeclass', { replace: true });
  }, [studentId, navigate]);

  // Close the mobile menu when the page changes
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Demo student details (same endpoint as before)
  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/demo/details/${studentId}`);
        if (!cancelled && response.data?.student) {
          setUserData({
            fullName: response.data.student.fullName,
            Image: response.data.student.Image,
            email: response.data.student.email,
            id: studentId,
          });
        }
      } catch (error) {
        console.error('Failed to load demo student details:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  // Demo heartbeat (same endpoint), sent right away and every 30s
  useEffect(() => {
    if (!studentId) return undefined;
    const sendHeartbeat = () =>
      fetch(`${process.env.REACT_APP_API_BASE_URL}/api/students/demobeat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ IdStudent: studentId }),
      }).catch((error) => console.error('Heartbeat failed:', error));

    sendHeartbeat();
    const intervalId = setInterval(sendHeartbeat, 30000);
    return () => clearInterval(intervalId);
  }, [studentId]);

  const handleLeave = () => {
    sessionStorage.removeItem('demoStudentId');
    navigate('/joinfreeclass');
  };

  const initials = (userData?.fullName || 'S')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('');

  const enrollUrl = `https://api.whatsapp.com/send/?phone=${PHONE}&text=${encodeURIComponent(
    `Hi! ${firstName ? `${firstName} just had` : 'We just had'} a free trial class and we'd like to enroll.`
  )}&type=phone_number&app_absent=0`;

  // Pass userData to the page, like before
  const childrenWithProps = React.Children.map(children, (child) =>
    React.isValidElement(child) ? React.cloneElement(child, { userData }) : child
  );

  if (!studentId) return null;

  return (
    <div className="rmdl" data-theme={theme}>
      {/* ---------- Top bar ---------- */}
      <header className="rmdl-top">
        <div className="rmdl-top-left">
          <button
            type="button"
            className="rmdl-menu-btn"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="rmdl-side"
          >
            <FontAwesomeIcon icon={menuOpen ? faXmark : faBars} />
          </button>
          <Link to="/" className="rmdl-logo" aria-label="RoboMind home">
            <img src={isLight ? LOGO_LIGHT : LOGO_DARK} alt="RoboMind" />
          </Link>
        </div>

        <div className="rmdl-top-right">
          <button
            type="button"
            className="rmdl-theme-btn"
            onClick={toggleTheme}
            aria-label={isLight ? 'Switch to dark mode' : 'Switch to light mode'}
            title={isLight ? 'Dark mode' : 'Light mode'}
          >
            <FontAwesomeIcon icon={isLight ? faMoon : faSun} />
          </button>

          <div className="rmdl-user">
          <span className="rmdl-avatar" aria-hidden="true">
            {userData?.Image ? <img src={userData.Image} alt="" /> : initials}
          </span>
          <span className="rmdl-user-name">{userData?.fullName || 'Student'}</span>
          </div>
        </div>
      </header>

      {/* ---------- Sidebar (drawer on mobile) ---------- */}
      <aside id="rmdl-side" className={`rmdl-side ${menuOpen ? 'is-open' : ''}`} aria-label="Student menu">
        <nav>
          <ul className="rmdl-nav">
            <li>
              <Link to="/DemoClass" className="rmdl-nav-item is-active" aria-current="page">
                <FontAwesomeIcon icon={faHome} />
                <span>Dashboard</span>
              </Link>
            </li>
            <li>
              <button
                type="button"
                className="rmdl-nav-item"
                onClick={() => {
                  setMenuOpen(false);
                  document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                <FontAwesomeIcon icon={faTags} />
                <span>Packages</span>
              </button>
            </li>
            <li>
              <a href="/Programs" target="_blank" rel="noopener noreferrer" className="rmdl-nav-item">
                <FontAwesomeIcon icon={faBookOpen} />
                <span>All courses</span>
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="rmdl-ext" />
              </a>
            </li>
            {LOCKED_ITEMS.map((item) => (
              <li key={item.label}>
                <span className="rmdl-nav-item is-locked" aria-disabled="true">
                  <FontAwesomeIcon icon={item.icon} />
                  <span>{item.label}</span>
                  <FontAwesomeIcon icon={faLock} className="rmdl-lock" title="Available after enrolling" />
                </span>
              </li>
            ))}
          </ul>
          <p className="rmdl-locked-note">
            <FontAwesomeIcon icon={faLock} /> Unlocks when you enroll
          </p>
        </nav>

        <div className="rmdl-side-bottom">
          <div className="rmdl-enroll">
            <strong>Enjoying the class?</strong>
            <span>Continue with the same teacher.</span>
            <a href={enrollUrl} target="_blank" rel="noopener noreferrer" className="rmdl-enroll-btn">
              <FontAwesomeIcon icon={faWhatsapp} /> Enroll on WhatsApp
            </a>
          </div>

          <button type="button" className="rmdl-leave" onClick={() => setShowLeave(true)}>
            <FontAwesomeIcon icon={faRightFromBracket} />
            Leave
          </button>
        </div>
      </aside>

      {menuOpen && <button type="button" className="rmdl-scrim" aria-label="Close menu" onClick={() => setMenuOpen(false)} />}

      {/* ---------- Page ---------- */}
      <main className="rmdl-main">{childrenWithProps}</main>

      {/* ---------- Leave confirmation ---------- */}
      <Modal
        isOpen={showLeave}
        onRequestClose={() => setShowLeave(false)}
        contentLabel="Leave the free class"
        className={`rmdl-modal ${isLight ? 'is-light' : ''}`}
        overlayClassName="rmdl-modal-overlay"
      >
        <h2 className="rmdl-modal-title">Leave the free class?</h2>
        <p className="rmdl-modal-text">You can come back any time with your class code.</p>
        <div className="rmdl-modal-actions">
          <button type="button" className="rmdl-modal-cancel" onClick={() => setShowLeave(false)}>
            Stay
          </button>
          <button type="button" className="rmdl-modal-confirm" onClick={handleLeave}>
            Yes, leave
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default DemoLayout;